import React, {Component} from 'react';
import ReactModal from 'react-modal';
import classnames from 'classnames';

import { IoMdCloseCircleOutline, IoMdHelpCircleOutline } from "react-icons/io";
import { ConnectedProps, connect } from 'react-redux';
import { RootState } from '../store/reducers/persistedReducer';
import { doCloseModal } from '../store/actions/modal';
import { selectLayoutCellById, selectModal, selectTheme } from '../store/selectors';
import { ModalType } from './ModalTypes';
import { CellDataTypes, ModalChart } from '@cluster/web-components'

import FormChannelSettings from '../Forms/ChannelSettings';
import FormSearch, { SearchTypes } from '../Forms/Search';
import ModalBrowser from '../ModalBrowser/ModalBrowser';
import Vimeo from '@u-wave/react-vimeo';
import { ClusterMarkdown } from '@cluster/web-components';

import styles from './Modal.module.scss'
import ShareDialog from '../Cell/ShareDialog';


ReactModal.setAppElement('#cluster-web')

class Modal extends Component<PropsFromRedux, {}> {

  getParent = (): HTMLElement => {
    return document.querySelector('#cluster-web > div') as HTMLElement;
  }

  render() {

    let modalTitle = this.props.modalTitle;
    let modalHelpUrl = (this.props.cellDetail as Exclude<CellDataTypes.CellDetailType, CellDataTypes.WebViewCellDetail>)?.modalDetail?.helpUrl;
    let modalContents: JSX.Element | undefined;
    let modalSize: "big" | "small" | "video" = "big";
    let modalNotes: string | undefined;

    // Chart Modal
    if (this.props.modalType === ModalType.Chart && this.props.cellDetail) {
      modalNotes = (this.props.cellDetail as CellDataTypes.CellDetailChartType).modalDetail.notes;

      modalSize = "big";
      modalContents = <ModalChart theme={this.props.theme} cellDetail={this.props.cellDetail} />;
    }
    // Form Modal
    else if (this.props.modalType === ModalType.Form && this.props.formName) {
      modalSize = "small";
      const forms: {[key: string]: {title: string, element: JSX.Element}} = {
        ChannelSettings: {
          title: "Channel Settings",
          element: <FormChannelSettings />
        },
        Search: {
          title: "Search",
          element: <FormSearch cellList={SearchTypes.LAYOUT} />
        },
        Favorites: {
          title: "Favorites",
          element: <FormSearch cellList={SearchTypes.FAVORITES} />
        }
      }

      if (forms.hasOwnProperty(this.props.formName)) {
        modalContents = forms[this.props.formName].element;
        modalTitle = forms[this.props.formName].title
      }
    }
    // URL Modal
    else if (this.props.modalType === ModalType.Url && this.props.modalUrl) {
      modalSize = "small";
      modalContents = <ModalBrowser url={this.props.modalUrl} title={modalTitle} />
    }
    // Vimeo Modal
    else if (this.props.modalType === ModalType.Vimeo && this.props.vimeoId) {
      modalSize = "video";
      modalContents = <Vimeo video={this.props.vimeoId} showByline={false} showPortrait={false} showTitle={false} autoplay />
    }
    // Share Cell Modal
    else if (this.props.modalType === ModalType.ShareCell && this.props.cellTitle && this.props.cellId && this.props.cellUrl) {
      modalSize = "small";
      modalContents = <ShareDialog cellId={this.props.cellId} cellTitle={this.props.cellTitle} cellUrl={this.props.cellUrl} />
    }


    return (
      <ReactModal
      parentSelector={this.getParent}
      isOpen={this.props.modalOpen}
      onRequestClose={() => this.props.disableClose ? false : this.props.doCloseModal()}
      className={classnames({
          [styles.modalSmall]: modalSize === "small",
          [styles.modalBig]: modalSize === "big",
          [styles.modalVideo]: modalSize === "video",
          [styles.hasNotes]: typeof modalNotes !== "undefined" && modalNotes !== "",
          [styles.hasPromptButton]: typeof this.props.promptButton !== "undefined" || this.props.promptButton !== false,
          [styles.keyboardOpen]: this.props.appKeyboardIsOpen
      })}
      style={{content: {["--keyboard-height" as any]: this.props.appKeyboardHeight + "px"}}}
      overlayClassName={styles.overlay}>
        <ModalTitleBar title={modalTitle} disableClose={this.props.disableClose} closeHandler={() => this.props.disableClose ? false : this.props.doCloseModal()} helpUrl={modalHelpUrl} />

        { modalNotes ? <div className={styles.modalNotes} dangerouslySetInnerHTML={{__html: ClusterMarkdown.render(modalNotes)}}></div> : ''}

        <div className={this.props.modalType === ModalType.Chart ? styles.chart : styles.innerModal}>
          {modalContents}
        </div>

        { this.props.promptButton && <div className={styles.promptWrapper}>
          <button className={styles.promptButton} onClick={() => this.props.promptButton?.action()}>{ this.props.promptButton.title }</button>
        </div>}
      </ReactModal>
    );
  }
}

const mapState = (state: RootState) => {
  const theme = selectTheme(state);
  const modal = selectModal(state);
  let cellId;
  let cellTitle;
  let cellUrl;
  let cellDetail;
  let formName;
  let modalUrl;
  let modalTitle;
  let disableClose = false;
  let promptButton;
  let vimeoId;

  if (modal?.data?.type === ModalType.Chart) {
    cellId = modal.data.cellId;
    cellDetail = selectLayoutCellById(state, cellId)?.cellDetail;
    modalTitle = cellDetail?.longTitle??"";
  }

  if (modal?.data?.type === ModalType.Form) {
    formName = modal.data.formName
  }

  if (modal?.data?.type === ModalType.Url) {
    modalUrl = modal.data.url;
    modalTitle = modal.data.title;
    disableClose = modal.data.disableClose??false;
    promptButton = modal.data.promptButton
  }

  if (modal?.data?.type === ModalType.Vimeo) {
    vimeoId = modal.data.vimeoId;
    modalTitle = modal.data.title;
  }

  if (modal?.data?.type === ModalType.ShareCell) {
    modalTitle = "Share " + modal.data.cellTitle;
    cellId = modal.data.cellId;
    cellTitle = modal.data.cellTitle;
    cellUrl = modal.data.cellUrl;
  }

  return {
    theme,
    modalOpen: modal.isOpen,
    modalType: modal?.data?.type,
    cellId,
    cellTitle,
    cellUrl,
    cellDetail,
    formName,
    modalUrl,
    modalTitle,
    disableClose,
    promptButton,
    vimeoId,
    appKeyboardIsOpen: state.system.appKeyboardIsOpen,
    appKeyboardHeight: state.system.appKeyboardHeight
  }
}

const mapDispatch = {
  doCloseModal
}
const connector = connect(
  mapState,
  mapDispatch
)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(Modal);

type ModalTitleBarProps = {
  title?: string,
  disableClose: boolean,
  closeHandler: (e: React.MouseEvent) => void,
  helpUrl?: string
}

function ModalTitleBar({title, disableClose, closeHandler, helpUrl}: ModalTitleBarProps) {
  const openHelp = (url?: string) => {
    window.open(url, '_blank');
  }

  return (
    <div className={styles.titleBar}>
      <div className={styles.title}>{ title }</div>
      <div className={styles.titleButtons}>
        {helpUrl && <IoMdHelpCircleOutline onClick={(() => openHelp(helpUrl))} className={styles.titleButton} size="1.5em" />}
        {!disableClose && <IoMdCloseCircleOutline onClick={closeHandler} className={styles.titleButton} size="1.5em" /> }
      </div>
    </div>
  )
}
